import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ProjectList } from '../../modules/project/ProjectList'
import { downloadProjectListing, getProjects, IProject } from '../../modules/project'
import { ProjectGrid } from '../../modules/project/ProjectGrid'
import { useSnackbar } from '../../../_theme/context/SnackbarContext'
import { KTIcon } from '../../../_theme/helpers'
import { ProjectMap } from './ProjectMap'
import { decodeBase64 } from '../../../utils/config'
import SimpleLoader from '../apps/components/SimpleLoader'
import { LogError } from '../../../utils/GlobalLogging'
import { ButtonGroup, Dropdown } from 'react-bootstrap'
import { getApiErrorMessage } from "../../../utils/utils";
import { isAbortedError } from "../../../utils/global";

const MyProjects = () => {
    const { label, filter, dashboard } = Object.fromEntries(new URLSearchParams(window.location.search));
    const { showSnackbar } = useSnackbar();
    const [loading, setLoading] = useState<boolean>(false);
    const [projects, setProjects] = useState<IProject[]>([]);
    const [viewMode, setViewMode] = useState<string>("list");
    const [page, setPage] = useState<number>(1);
    const [perPage, setPerPage] = useState<number>(10);
    const [search, setSearch] = useState<string>("");
    const [total, setTotal] = useState<number>(0);

    const [sortKey, setSortKey] = useState<string | null>(null);
    const [sortOrder, setSortOrder] = useState<"asc" | "desc" | null>("asc");
    const abortRef = useRef<AbortController | null>(null);
    const parsedFilters = useMemo(
        () => JSON.parse(filter ? decodeBase64(filter) : '{}'),
        [filter]
    );
    const mapLabel = useMemo(
        () => (label ? decodeBase64(label).replace(' & ', '_') : ''),
        [label]
    );

    const handleSortChange = (key: string, order: "asc" | "desc" | null) => {
        setSortKey(key);
        setSortOrder(order);
    };

    const fetchProjects = useCallback(async () => {
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        try {
            setLoading(true);
            const dashboard_type = decodeBase64(dashboard).toLowerCase().trim().replace(/\s+/g, "_");
            const projectResponse = await getProjects(
                JSON.parse(filter ? decodeBase64(filter) : '{}'),
                dashboard_type,
                "Yes",
                (label && decodeBase64(label).replace(' & ', '_')),
                page,
                perPage,
                search,
                sortKey,
                sortOrder,
                true,
                controller.signal
            );
            if (controller.signal.aborted) return;
            if (projectResponse.status == "ok") {
                setProjects(projectResponse.data.projects)
                setTotal(projectResponse.data.pagination?.total_records ?? 0);
            } else {
                showSnackbar(projectResponse.message, 'danger');
            }
        } catch (error) {
            if (isAbortedError(error)) return;
            LogError("Something went wrong", error, "MyProjects");
            showSnackbar(getApiErrorMessage(error, "Failed to fetch projects:"), 'danger');
        } finally {
            if (!controller.signal.aborted) setLoading(false);
        }
    }, [dashboard, filter, label, page, perPage, search, sortKey, sortOrder, showSnackbar]);

    const handleDownloadProjectListing = async (fileType: string) => {
        try {
            const dashboard_type = decodeBase64(dashboard).toLowerCase().trim().replace(/\s+/g, "_");
            const projectResponse = await downloadProjectListing(JSON.parse(filter ? decodeBase64(filter) : '{}'), dashboard_type, (label && decodeBase64(label).replace(' & ', '_')), fileType);
            if (projectResponse.status == "ok") {
                window.location.href = projectResponse.data.url;
            } else {
                showSnackbar(projectResponse.message, 'danger');
            }
        } catch (error) {
            LogError("Something went wrong", error, "MyProjects");
            showSnackbar(getApiErrorMessage(error, "Failed to Download Projects:"), 'danger');
        }
    }
    useEffect(() => {
        if (search) {
            const timeoutId = setTimeout(() => {
                fetchProjects();
            }, 1000);

            return () => {
                clearTimeout(timeoutId);
                abortRef.current?.abort();
            };
        }
        fetchProjects();
        return () => abortRef.current?.abort();
    }, [fetchProjects, search]);

    useEffect(() => {
        const lastActiveTab = sessionStorage.getItem('viewMode') || 'list';
        if (lastActiveTab) {
            setViewMode(lastActiveTab);
            sessionStorage.removeItem('viewMode');
        }
    }, []);

    const loadView = () => {
        switch (viewMode) {
            case "list":
                return <ProjectList projects={projects} page={page} perPage={perPage} total={total} setPage={setPage} setPerPage={setPerPage} sortKey={sortKey} sortOrder={sortOrder} handleSortChange={handleSortChange} />;
            case "grid":
                return <ProjectGrid projects={projects} />;
            case "map":
                return <ProjectMap filters={parsedFilters} search={search} label={mapLabel} dashboard={dashboard} />;
            default:
                return <></>;
        }
    }
    return (
        <div className={`card mb-5`}>
            {/* begin::Header */}
            {loading && <SimpleLoader className={"bg-black-50 z-2"} />}

            <div className='card-header border-0 pt-5'>
                <h3 className='card-title align-items-start flex-column'>
                    <span className='card-label fw-bolder text-gray-700 fs-3 mb-1'>{label ? `${decodeBase64(label)} (${total})` : "Projects"}</span>
                </h3>
                <div className='d-flex justify-content-end flex-wrap align-items-center py-1 gap-2'>
                    <Dropdown as={ButtonGroup} className='h-100'>
                        <Dropdown.Toggle variant="light-primary" className="py-0">
                            Export
                        </Dropdown.Toggle>

                        <Dropdown.Menu>
                            <Dropdown.Item
                                onClick={() => handleDownloadProjectListing("excel")}
                            >
                                Export to Excel
                            </Dropdown.Item>
                            <Dropdown.Item
                                onClick={() => handleDownloadProjectListing("pdf")}
                            >
                                Export to PDF
                            </Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                    <input
                        type='text'
                        className='form-control form-control-md h-100'
                        placeholder='Search'
                        style={{ width: '300px' }}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                    />
                    <div className='btn-group btn-group-sm gap-2' role='group' aria-label='View mode'>
                        <button
                            type='button'
                            className={`btn btn-sm btn-icon btn-light btn-color-muted btn-active-primary ${viewMode === 'list' ? 'active' : ''}`}
                            data-kt-menu-trigger='click'
                            data-kt-menu-placement='bottom-end'
                            data-kt-menu-flip='top-end'
                            onClick={() => {
                                setViewMode('list'), sessionStorage.setItem('viewMode', 'list');
                            }}
                        >
                            <KTIcon iconType='duotone' iconName='row-horizontal' className='fs-1' />
                        </button>

                        {/* <button
                type='button'
                className={`btn btn-sm btn-icon btn-light btn-color-muted btn-active-primary ${viewMode === 'grid' ? 'active' : ''}`}
                data-kt-menu-trigger='click'
                data-kt-menu-placement='bottom-end'
                data-kt-menu-flip='top-end'
                onClick={() => setViewMode('grid')}
              >
                <KTIcon iconType='duotone' iconName='element-plus' className='fs-1' />
              </button> */}

                        <button
                            type='button'
                            className={`btn btn-sm btn-icon btn-light btn-color-muted btn-active-primary ${viewMode === 'map' ? 'active' : ''}`}
                            data-kt-menu-trigger='click'
                            data-kt-menu-placement='bottom-end'
                            data-kt-menu-flip='top-end'
                            onClick={() => {
                                setViewMode('map'), sessionStorage.setItem('viewMode', 'map');
                            }}
                        >
                            <KTIcon iconType='duotone' iconName='map' className='fs-1' />
                        </button>
                    </div>
                </div>

            </div>
            {/* end::Header */}
            {/* begin::Body */}
            <div className='card-body py-3'>
                {loadView()}
            </div>
            {/* begin::Body */}
        </div>

    )
}
export { MyProjects }
